import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { AlertTriangle, X } from 'lucide-react'

interface MemoryWarning {
  memory_percent: number
  swap_percent?: number | null
  current_size: string
  current_memory_gb: number
  suggested_size: string | null
  suggested_memory_gb: number | null
  instance_name: string
}

interface MemoryWarningBannerProps {
  projectId: number
  warning: MemoryWarning
  isFreePlan: boolean
  onDismiss: () => void
}

export function MemoryWarningBanner({
  projectId,
  warning,
  isFreePlan,
  onDismiss
}: MemoryWarningBannerProps) {
  const { t } = useTranslation('conversations')

  const handleUpgrade = () => {
    if (!warning.suggested_size) return
    router.post(`/projects/${projectId}/resize_instance`, {
      size: warning.suggested_size
    }, {
      onFinish: () => onDismiss()
    })
  }

  return (
    <div className="mx-4 mb-2 rounded-xl border border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950/30 p-4 flex-shrink-0 relative">
      <button
        onClick={onDismiss}
        className="absolute top-2 right-2 p-1 rounded-md hover:bg-orange-200 dark:hover:bg-orange-800 text-orange-600 dark:text-orange-400"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-orange-800 dark:text-orange-200">
            {t('memoryWarning.title', { percent: warning.memory_percent })}
          </div>
          <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
            {t('memoryWarning.description', {
              percent: warning.memory_percent,
              size: warning.current_size,
              memoryGb: warning.current_memory_gb
            })}
            {warning.swap_percent != null && warning.swap_percent > 0 && (
              <span className="block mt-0.5">
                {t('memoryWarning.swapDescription', { percent: warning.swap_percent })}
              </span>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2">
            {isFreePlan ? (
              <p className="text-xs text-orange-600 dark:text-orange-400">
                {t('memoryWarning.contactSupport')}
              </p>
            ) : warning.suggested_size ? (
              <Button
                variant="outline"
                size="sm"
                className="h-auto py-2 px-3 border-orange-300 dark:border-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/50 text-xs"
                onClick={handleUpgrade}
              >
                {t('memoryWarning.upgradeButton', {
                  size: warning.suggested_size,
                  memoryGb: warning.suggested_memory_gb
                })}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="sm"
              className="h-auto py-2 px-3 text-xs text-orange-600 dark:text-orange-400"
              onClick={onDismiss}
            >
              {t('memoryWarning.dismissButton')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
